// FILE: components/explore/_shared/mappers.ts
import type { PathItem, ExamItem, ReviewItem } from "./types";

/* ═══ Constants ═══ */

export const GRADIENTS = [
  "from-[#0f3a4a] to-[#1a5570]",
  "from-[#1e6a8a] to-[#2a8aaa]",
  "from-[#ea580c] to-[#f59e0b]",
  "from-[#1e5a70] to-[#2a7a9a]",
  "from-[#0a2a36] to-[#1a4a56]",
  "from-[#7c3aed] to-[#a855f7]",
];

export const AVATAR_COLORS = [
  "#0f3a4a", "#7c3aed", "#ea580c", "#0a2a36",
  "#1e5a70", "#16a34a", "#dc2626", "#2563eb",
];

/* ═══ Pure helpers ═══ */

export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ngày trước`;
  const weeks = Math.floor(days / 7);
  return `${weeks} tuần trước`;
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function hashColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

/* ═══ DB row → UI item ═══ */

export function mapPath(row: any, idx: number): PathItem {
  const author = row.creator?.full_name ?? row.creator_name ?? row.creator?.username ?? row.creator_username ?? "Tác giả";
  const authorId = row.creator?.id ?? row.user_id;
  const authorUsername = row.creator?.username ?? row.creator_username ?? null;
  const orgName = row.org?.name;
  const orgSlug = row.org?.slug;
  const positionName = row.position?.name;
  const positionSlug = row.position?.slug;
  const tags = row.tags?.length > 0 ? row.tags : (orgName ? [orgName] : []);
  return {
    id: row.id,
    title: row.title ?? "Lộ trình",
    author,
    authorId,
    authorUsername,
    emoji: row.emoji ?? "📚",
    tags,
    orgName,
    orgHref: orgSlug ? `/orgs/${orgSlug}` : undefined,
    positionName,
    positionHref: positionSlug ? `/positions/${positionSlug}` : undefined,
    rating: row.average_rating ?? 0,
    students: row.total_enrolled ?? 0,
    questions: row.total_questions ?? 0,
    demoQuestions: row.demo_limit ?? Math.min(10, row.total_questions ?? 0),
    price: (!row.price || row.price === 0) ? "free" : row.price,
    gradient: GRADIENTS[idx % GRADIENTS.length],
    slug: row.slug ?? "",
    isUnlocked: row.is_unlocked ?? false,
    progress: row.progress ?? 0,
    isOwner: row.is_owner ?? false,
  };
}

export function mapExam(row: any): ExamItem {
  const author = row.creator?.full_name ?? row.creator?.username ?? "Tác giả";
  const orgName = row.org?.name;
  const tags = row.tags?.length > 0 ? row.tags : (orgName ? [orgName] : []);
  const timeStr = row.time_limit ? `${row.time_limit} phút` : "—";
  return {
    id: row.id,
    title: row.title ?? "Đề thi",
    author,
    emoji: row.emoji ?? "📝",
    tags,
    questions: row.total_questions ?? 0,
    time: timeStr,
    attempts: row.attempt_count ?? 0,
    likes: Math.floor((row.attempt_count ?? 0) * 0.3),
    price: (!row.price || row.price === 0) ? "free" : row.price,
    difficulty: "Trung bình",
    slug: row.slug ?? "",
    isUnlocked: row.is_unlocked ?? false,
    progress: row.progress ?? 0,
    isOwner: row.is_owner ?? false,
  };
}

export function mapReview(row: any): ReviewItem {
  const author = row.user?.full_name ?? row.user?.username ?? "Ẩn danh";
  const orgName = row.org?.name ?? "—";
  return {
    id: row.id,
    author,
    avatar: getInitials(author),
    avatarColor: hashColor(author),
    stars: row.rating ?? 5,
    title: row.title ?? "Review",
    text: (row.content ?? "").replace(/<[^>]*>/g, "").slice(0, 200),
    time: timeAgo(row.created_at),
    likes: 0,
    comments: 0,
    org: orgName,
    slug: row.slug ?? "",
  };
}